import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
  useMemo,
} from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import './datepickerStyles.css';
import HourPreferences from './HourPreferences';
import WeekdayPreferences from './WeekdayPreferences';
import TerminalPreferences from './TerminalPreferences';
import { editAutoBookContainerData, SaveApiResMessage } from '@/actions';
import { Icons } from '@/assets';
import { ReactSVG } from 'react-svg';
import { useNotify } from 'react-admin';
import { CustomCircularProgress } from '@/components/Loading/CustomCircularProgress';
import { countActiveTerminalPauses } from './terminalPauseUtils';
import { TerminalPausedBadgePill } from './TerminalPausedBadge';

function normalizeTerminalPrefs(t) {
  if (t != null && typeof t === 'object' && !Array.isArray(t)) return t;
  return {};
}

function normalizeWeekdayPrefsState(w) {
  if (w != null && typeof w === 'object' && !Array.isArray(w)) return { ...w };
  return {};
}

function sortKeysDeep(value) {
  if (value === null || value === undefined) return value;
  if (typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(sortKeysDeep);
  const sorted = {};
  for (const k of Object.keys(value).sort()) {
    sorted[k] = sortKeysDeep(value[k]);
  }
  return sorted;
}

function serializePrefsPayload(p) {
  return JSON.stringify({
    hour_preferences: sortKeysDeep(p.hour_preferences ?? []),
    weekly_preferences: sortKeysDeep(p.weekly_preferences ?? []),
    weekday_preferences: sortKeysDeep(
      normalizeWeekdayPrefsState(p.weekday_preferences)
    ),
    terminal_preferences: sortKeysDeep(
      normalizeTerminalPrefs(p.terminal_preferences)
    ),
  });
}

function clonePrefsFromSettings(s) {
  if (!s) {
    return {
      hour_preferences: [],
      weekly_preferences: [],
      weekday_preferences: {},
      terminal_preferences: {},
    };
  }
  try {
    return {
      hour_preferences: JSON.parse(
        JSON.stringify(s.hour_preferences ?? [])
      ),
      weekly_preferences: JSON.parse(
        JSON.stringify(s.weekly_preferences ?? [])
      ),
      weekday_preferences: JSON.parse(
        JSON.stringify(normalizeWeekdayPrefsState(s.weekday_preferences))
      ),
      terminal_preferences: JSON.parse(
        JSON.stringify(normalizeTerminalPrefs(s.terminal_preferences))
      ),
    };
  } catch {
    return {
      hour_preferences: [...(s.hour_preferences ?? [])],
      weekly_preferences: [...(s.weekly_preferences ?? [])],
      weekday_preferences: normalizeWeekdayPrefsState(s.weekday_preferences),
      terminal_preferences: { ...normalizeTerminalPrefs(s.terminal_preferences) },
    };
  }
}

const AutobookTimeDateSelector = ({
  settings,
  AutobookId,
  saving_identifier,
  clickConatiner,
  autobookMode,
  lfdDate,
  setHistoryList,
  activeSection,
  setActiveSection,
  persistToContainer = true,
  onPreferencesChange,
  preferencesSyncKey,
  hideAddRangePrioritySelect = false,
  hidePreferencesHeader = false,
  rulesWeekdayDesign = false,
  rulesTerminalDesign = false,
}) => {
  const [embeddedAccordionSection, setEmbeddedAccordionSection] =
    useState('hour');
  const accordionSection = persistToContainer
    ? activeSection
    : embeddedAccordionSection;

  const toggleAccordionSection = (sec) => {
    if (persistToContainer) {
      setActiveSection(activeSection === sec ? null : sec);
    } else {
      setEmbeddedAccordionSection((cur) => (cur === sec ? null : sec));
    }
  };

  const [circleHourLoading, setCircleHourLoading] = useState(false);
  const [circleWeekdayLoading, setCircleWeekdayLoading] = useState(false);
  const [circleTerminalLoading, setCircleTerminalLoading] = useState(false);
  const [rangeDraft, setRangeDraft] = useState([null, null]);
  const [rangePriority, setRangePriority] = useState('High');
  const [selectedRanges, setSelectedRanges] = useState([]);
  const [weekdayPrefs, setWeekdayPrefs] = useState({});
  const [terminalPrefs, setTerminalPrefs] = useState({});
  const [hourSelections, setHourSelections] = useState([]);
  const [hourDraft, setHourDraft] = useState([null, null]);
  const [hourPriority, setHourPriority] = useState('High');

  const notify = useNotify();

  const terminalPausedCount = useMemo(
    () => countActiveTerminalPauses(terminalPrefs),
    [terminalPrefs]
  );

  const prevHourSelections = useRef(null);
  const prevSelectedRanges = useRef(null);
  const prevWeekdayPrefs = useRef(null);
  const prevTerminalPrefs = useRef(null);
  const skipNextPersistRef = useRef(false);

  const embeddedLastInboundSigRef = useRef(null);
  const embeddedLastEmitSigRef = useRef(null);
  const preferencesSyncKeyPrevRef = useRef(preferencesSyncKey);
  /** بعد از push از والد، یک بار effect emit را بی‌صدا کن تا حلقهٔ setState↔والد قطع شود */
  const skipNextEmbedEmitRef = useRef(false);
  const onPreferencesChangeRef = useRef(onPreferencesChange);
  onPreferencesChangeRef.current = onPreferencesChange;

  useLayoutEffect(() => {
    if (!settings) return;

    if (!persistToContainer) {
      if (preferencesSyncKeyPrevRef.current !== preferencesSyncKey) {
        preferencesSyncKeyPrevRef.current = preferencesSyncKey;
        embeddedLastInboundSigRef.current = null;
        embeddedLastEmitSigRef.current = null;
      }
      const inboundSig = serializePrefsPayload(settings);
      if (inboundSig === embeddedLastInboundSigRef.current) {
        return;
      }
      const stateSig = serializePrefsPayload({
        hour_preferences: hourSelections,
        weekly_preferences: selectedRanges,
        weekday_preferences: normalizeWeekdayPrefsState(weekdayPrefs),
        terminal_preferences: normalizeTerminalPrefs(terminalPrefs),
      });
      if (stateSig === inboundSig) {
        embeddedLastInboundSigRef.current = inboundSig;
        embeddedLastEmitSigRef.current = inboundSig;
        return;
      }
      embeddedLastInboundSigRef.current = inboundSig;
      skipNextEmbedEmitRef.current = true;
      embeddedLastEmitSigRef.current = inboundSig;
    }

    const s = settings;
    if (!persistToContainer) {
      const c = clonePrefsFromSettings(s);
      setTerminalPrefs(c.terminal_preferences);
      setWeekdayPrefs(c.weekday_preferences);
      setHourSelections(c.hour_preferences);
      setSelectedRanges(c.weekly_preferences);
      prevHourSelections.current = c.hour_preferences;
      prevSelectedRanges.current = c.weekly_preferences;
      prevWeekdayPrefs.current = c.weekday_preferences;
      prevTerminalPrefs.current = c.terminal_preferences;
    } else {
      const nextTerminal = normalizeTerminalPrefs(s.terminal_preferences);
      setTerminalPrefs(nextTerminal);
      const nextWeekday = normalizeWeekdayPrefsState(s.weekday_preferences);
      setWeekdayPrefs(nextWeekday);
      setHourSelections(s.hour_preferences ?? []);
      setSelectedRanges(s.weekly_preferences ?? []);
      prevHourSelections.current = s.hour_preferences ?? [];
      prevSelectedRanges.current = s.weekly_preferences ?? [];
      prevWeekdayPrefs.current = nextWeekday;
      prevTerminalPrefs.current = nextTerminal;
      // Settings are freshly hydrated from server; skip one persist cycle.
      skipNextPersistRef.current = true;
    }
    // فقط وقتی settings / کلید همگام عوض شود؛ state محلی عمداً در deps نیست (مقایسهٔ stateSig همان رندر والد را می‌بیند)
  }, [clickConatiner, settings, preferencesSyncKey, persistToContainer]);

  useEffect(() => {
    if (!persistToContainer) return;
    if (skipNextPersistRef.current) {
      skipNextPersistRef.current = false;
      return;
    }

    const hasInitialData = prevHourSelections.current !== null;

    if (hasInitialData) {
      if (accordionSection === 'hour') {
        setCircleHourLoading(true);
      }
      if (accordionSection === 'weekday') {
        setCircleWeekdayLoading(true);
      }
      if (accordionSection === 'terminal') {
        setCircleTerminalLoading(true);
      }
    }

    const nextTerminal = normalizeTerminalPrefs(terminalPrefs);
    const data = {
      saving_identifier: saving_identifier,
      status: autobookMode.status ? 'ENABLED' : 'DISABLED',
      hour_preferences: hourSelections,
      weekly_preferences: selectedRanges,
      weekday_preferences: normalizeWeekdayPrefsState(weekdayPrefs),
      terminal_preferences: nextTerminal,
    };

    const hasChanges =
      hasInitialData &&
      (JSON.stringify(hourSelections) !==
        JSON.stringify(prevHourSelections.current) ||
        JSON.stringify(selectedRanges) !==
          JSON.stringify(prevSelectedRanges.current) ||
        JSON.stringify(weekdayPrefs) !==
          JSON.stringify(prevWeekdayPrefs.current) ||
        JSON.stringify(nextTerminal) !==
          JSON.stringify(prevTerminalPrefs.current));

    if (AutobookId && saving_identifier && hasChanges) {
      editAutoBookContainerData({ AutobookId, data })
        .then((res) => {
          setHistoryList(res.autobook?.event_history.reverse());
          prevHourSelections.current = hourSelections;
          prevSelectedRanges.current = selectedRanges;
          prevWeekdayPrefs.current = weekdayPrefs;
          prevTerminalPrefs.current = nextTerminal;
        })
        .catch((err) => {
          SaveApiResMessage({
            title: 'Error',
            message: err || 'Failed to update autobook preferences',
            mode: 'error',
          });
        })
        .finally(() => {
          setTimeout(() => {
            setCircleHourLoading(false);
            setCircleWeekdayLoading(false);
            setCircleTerminalLoading(false);
          }, 500);
        });
    } else if (hasInitialData && !hasChanges) {
      setTimeout(() => {
        setCircleHourLoading(false);
        setCircleWeekdayLoading(false);
        setCircleTerminalLoading(false);
      }, 100);
    }
  }, [
    persistToContainer,
    terminalPrefs,
    weekdayPrefs,
    hourSelections,
    selectedRanges,
    AutobookId,
    saving_identifier,
    accordionSection,
    autobookMode?.status,
    setHistoryList,
  ]);

  useEffect(() => {
    if (persistToContainer) return;
    const notifyParent = onPreferencesChangeRef.current;
    if (typeof notifyParent !== 'function') return;

    const payload = {
      hour_preferences: hourSelections ?? [],
      weekly_preferences: selectedRanges ?? [],
      weekday_preferences: normalizeWeekdayPrefsState(weekdayPrefs),
      terminal_preferences: normalizeTerminalPrefs(terminalPrefs),
    };
    const sig = serializePrefsPayload(payload);
    if (skipNextEmbedEmitRef.current) {
      skipNextEmbedEmitRef.current = false;
      embeddedLastEmitSigRef.current = sig;
      return;
    }
    if (sig === embeddedLastEmitSigRef.current) return;
    embeddedLastEmitSigRef.current = sig;
    notifyParent(payload);
  }, [hourSelections, selectedRanges, weekdayPrefs, terminalPrefs, persistToContainer]);

  return (
    <div id="aytobookPreferences">
      <div className="autobook-wrapper">
        {!hidePreferencesHeader && (
          <div className="legend-container">
            <div className="legend-titles">
              <span className="legend-title">Autobook Preferences</span>
              <span className="legend-description">
                Set your preferred time slots, days, and terminals for
                auto-scheduling.
              </span>
            </div>
            <div className="legend">
              <div className="legend-item">
                <div className="box-green"></div> High
              </div>
              <div className="legend-item">
                <div className="box-orange"></div> Medium
              </div>
              <div className="legend-item">
                <div className="box-yellow"></div> Low
              </div>
              <div className="legend-item">
                <div className="box-red"></div> Exclude
              </div>
              <div className="legend-item legend-item-paused">
                <TerminalPausedBadgePill legend />
              </div>
            </div>
          </div>
        )}

        <div className="accordion">
          <div
            className={`accordion-item ${
              accordionSection === 'hour' ? 'active' : ''
            }`}>
            <div
              className={`accordion-header ${
                accordionSection === 'hour' ? 'active' : ''
              }`}
              onClick={() => toggleAccordionSection('hour')}>
              <span className="selctedtextRow">
                Hour Preferences{'   '}|
                <div className="selctedtext">
                  {hourSelections?.length || 0}
                  {'  '}{rulesWeekdayDesign ?  'Excluded' : 'Selected'}
                  {circleHourLoading ? (
                    <span className="circle-loading">
                      <CustomCircularProgress className="w-4 h-4 border-primary-text" />
                    </span>
                  ) : (
                    ''
                  )}
                </div>
              </span>
              <span className="accordion-icon">
                {accordionSection === 'hour' ? (
                  <ReactSVG className="arrow-icon" src={Icons.arrowTop} />
                ) : (
                  <ReactSVG className="arrow-icon" src={Icons.arrowDown} />
                )}
              </span>
            </div>
            {accordionSection === 'hour' && (
              <div className="accordion-content">
                <HourPreferences
                  hourSelections={hourSelections}
                  hourDraft={hourDraft}
                  hourPriority={hourPriority}
                  setHourSelections={setHourSelections}
                  setHourDraft={setHourDraft}
                  setHourPriority={setHourPriority}
                  hidePrioritySelect={hideAddRangePrioritySelect}
                />
              </div>
            )}
          </div>

          <div
            className={`accordion-item ${
              accordionSection === 'weekday' ? 'active' : ''
            }`}>
            <div
              className={`accordion-header ${
                accordionSection === 'weekday' ? 'active' : ''
              }`}
              onClick={() => toggleAccordionSection('weekday')}>
              <span className="selctedtextRow">
                Weekday Preferences{'   '}|
                <div className="selctedtext">
                  {selectedRanges?.length || 0}
                  {'  '}{rulesWeekdayDesign ?  'Excluded' : 'Selected'}
                  {circleWeekdayLoading ? (
                    <span className="circle-loading">
                      <CustomCircularProgress className="w-4 h-4 border-primary-text" />
                    </span>
                  ) : (
                    ''
                  )}
                </div>
              </span>
              <span className="accordion-icon">
                {accordionSection === 'weekday' ? (
                  <ReactSVG className="arrow-icon" src={Icons.arrowTop} />
                ) : (
                  <ReactSVG className="arrow-icon" src={Icons.arrowDown} />
                )}
              </span>
            </div>
            {accordionSection === 'weekday' && (
              <div className="accordion-content">
                <WeekdayPreferences
                  weekdayPrefs={weekdayPrefs}
                  setWeekdayPrefs={setWeekdayPrefs}
                  rangeDraft={rangeDraft}
                  setRangeDraft={setRangeDraft}
                  rangePriority={rangePriority}
                  setRangePriority={setRangePriority}
                  selectedRanges={selectedRanges}
                  setSelectedRanges={setSelectedRanges}
                  lfdDate={lfdDate}
                  hideRangePrioritySelect={hideAddRangePrioritySelect}
                  rulesWeekdayDesign={rulesWeekdayDesign}
                />
              </div>
            )}
          </div>

          <div
            className={`accordion-item ${
              accordionSection === 'terminal' ? 'active' : ''
            }`}>
            <div
              className={`accordion-header ${
                accordionSection === 'terminal' ? 'active' : ''
              }`}
              onClick={() => toggleAccordionSection('terminal')}>
              <span className="selctedtextRow">
                Terminal Preferences{'   '}|
                <div className="selctedtext">
                  {Object.values(terminalPrefs || {}).filter(
                    (item) =>
                      String(item?.priority || '').toUpperCase() === 'EXCLUDE'
                  ).length || 0}{' '}
                  Excluded
                  {terminalPausedCount > 0 ? (
                    <>
                      {' · '}
                      <span className="terminal-paused-header-count">
                        {terminalPausedCount} Paused
                      </span>
                    </>
                  ) : null}
                  {circleTerminalLoading ? (
                    <span className="circle-loading">
                      <CustomCircularProgress className="w-4 h-4 border-primary-text" />
                    </span>
                  ) : (
                    ''
                  )}
                </div>
              </span>
              <span className="accordion-icon">
                {accordionSection === 'terminal' ? (
                  <ReactSVG className="arrow-icon" src={Icons.arrowTop} />
                ) : (
                  <ReactSVG className="arrow-icon" src={Icons.arrowDown} />
                )}
              </span>
            </div>
            {accordionSection === 'terminal' && (
              <div className="accordion-content">
                <TerminalPreferences
                  terminalPrefs={terminalPrefs}
                  setTerminalPrefs={setTerminalPrefs}
                  rulesTerminalDesign={rulesTerminalDesign}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
export default AutobookTimeDateSelector;
